import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const INITIAL_FORM = {
  applicationName: '',
  applicationDescription: '',
  migrateDatabaseData: '',
  dbAnalysisRequested: false,
  applicationTemplate: '',
  dominoServerVersion: '',
  approximateUsers: '',
  approximateDocuments: '',
  approximateDatabaseSize: '',
  applicationCriticality: '',
  applicationFeatures: [],
  externalIntegrationsDetails: '',
  scheduledAgentsDetails: '',
  targetPlatform: '',
  priority: '',
  hasMigrationTeam: '',
  preferredEngagement: '',
  desiredTimeline: '',
  hasComplianceRequirements: '',
  complianceDetails: '',
  additionalNotes: '',
};

const APP_FEATURE_OPTIONS = [
  'Workflow/Approvals',
  'External Integration',
  'Document-Level-security',
  'Custom ACL roles',
  'Attachments',
];

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@rivit.ca';
const SUPPORT_PHONE = process.env.REACT_APP_SUPPORT_PHONE || '647.930.1711';

export default function SubmitPage() {
  const { user, refreshUser } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(INITIAL_FORM);
  const [dxlFile, setDxlFile] = useState(null);
  const [fileValid, setFileValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Validation overlay
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Access code modal
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [redeemingCode, setRedeemingCode] = useState(false);

  const remaining = Math.max(0, (user.submissionLimit || 1) - (user.submissionsUsed || 0));

  // Auto-show access code modal if user has no remaining submissions
  useEffect(() => {
    if (remaining <= 0) {
      setShowAccessCodeModal(true);
    }
  }, [remaining]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleFeature = (feature) => {
    setForm((prev) => {
      const exists = prev.applicationFeatures.includes(feature);
      return {
        ...prev,
        applicationFeatures: exists
          ? prev.applicationFeatures.filter((item) => item !== feature)
          : [...prev.applicationFeatures, feature],
      };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'dxl' && ext !== 'xml' && ext !== 'zip') {
      addToast('Please select a .dxl, .xml, or .zip file.', 'error');
      e.target.value = '';
      setDxlFile(null);
      setFileValid(false);
      return;
    }

    // Show validation overlay and validate on server
    setDxlFile(file);
    setFileValid(false);
    setValidating(true);
    setValidationError('');

    try {
      const formData = new FormData();
      formData.append('dxlFile', file);

      const res = await fetch('/api/submissions/validate', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();
      if (!res.ok || !data.valid) {
        setValidationError(data.error || 'File validation failed.');
        setDxlFile(null);
        setFileValid(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setFileValid(true);
      }
    } catch (err) {
      setValidationError('Could not validate file. Please try again.');
      setDxlFile(null);
      setFileValid(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setValidating(false);
    }
  };

  const validate = () => {
    if (!form.applicationName.trim()) return 'Application Name is required.';
    if (form.approximateUsers && Number(form.approximateUsers) < 0) return 'Approximate active users must be a valid number.';
    if (form.approximateDocuments && Number(form.approximateDocuments) < 0) return 'Approximate documents/records must be a valid number.';
    if (!form.dominoServerVersion) return 'Please select Domino Server Version.';
    if (!form.migrateDatabaseData) return 'Please indicate whether you want to migrate the database data.';
    if (!form.targetPlatform) return 'Please select a Target Platform.';
    if (!form.priority) return 'Please select a Priority level.';
    if (!form.hasMigrationTeam) return 'Please select whether your organization has a migration team.';
    if (!form.preferredEngagement) return 'Please select a Migration Delivery Model.';
    if (!form.desiredTimeline) return 'Please select a desired migration timeline.';
    if (!form.hasComplianceRequirements) return 'Please indicate if there are compliance requirements.';
    if (form.hasComplianceRequirements === 'Yes' && !form.complianceDetails.trim()) {
      return 'Please describe compliance requirements.';
    }
    if (form.applicationFeatures.includes('External Integration') && !form.externalIntegrationsDetails.trim()) {
      return 'Please describe the external integrations.';
    }
    if (!dxlFile || !fileValid) return 'Please upload a valid DXL file.';
    return null;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      addToast(error, 'error');
      return;
    }

    // Check submission limit
    if (remaining <= 0) {
      setShowAccessCodeModal(true);
      return;
    }

    setShowConfirm(true);
  };

  const handleRedeemCode = async () => {
    if (!accessCode.trim()) {
      addToast('Please enter an access code.', 'error');
      return;
    }
    setRedeemingCode(true);
    try {
      const res = await fetch('/api/submissions/redeem-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: accessCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(data.message, 'success');
      setAccessCode('');
      setShowAccessCodeModal(false);
      await refreshUser();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setRedeemingCode(false);
    }
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setSubmitting(true);

    try {
      const payload = {
        ...form,
        organizationName: user.organizationName,
        contactName: user.fullName,
        contactEmail: user.email,
        approximateUsers: form.approximateUsers ? Number(form.approximateUsers) : 0,
        approximateDocuments: form.approximateDocuments ? Number(form.approximateDocuments) : 0,
        hasWorkflowApprovals: form.applicationFeatures.includes('Workflow/Approvals'),
        hasEmailNotifications: false,
        hasIntegrations: form.applicationFeatures.includes('External Integration'),
        integrationDetails: form.externalIntegrationsDetails,
        hasScheduledAgents: false,
        hasCustomSecurity:
          form.applicationFeatures.includes('Document-Level-security') ||
          form.applicationFeatures.includes('Custom ACL roles'),
        securityDetails: '',
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));
      formData.append('dxlFile', dxlFile);

      const res = await fetch('/api/submissions', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.limitReached) {
          setShowAccessCodeModal(true);
          return;
        }
        throw new Error(data.message);
      }

      await refreshUser();
      addToast('Submission created successfully!', 'success');
      navigate('/dashboard');
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="submit-page">
      <div className="submit-header">
        <h1>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
          New Submission
        </h1>
        <p className="submit-subtitle">Submit a single Lotus Notes application for migration analysis</p>
      </div>

      {/* Submission Quota Banner */}
      <div className={`quota-banner ${remaining <= 0 ? 'quota-banner-warn' : ''}`}>
        <div className="quota-info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <span>
            <strong>{remaining}</strong> free submission{remaining !== 1 ? 's' : ''} remaining
            <span className="quota-detail"> ({user.submissionsUsed || 0} used of {user.submissionLimit || 1})</span>
          </span>
        </div>
        {remaining <= 0 && (
          <button className="btn btn-sm btn-outline" onClick={() => setShowAccessCodeModal(true)}>
            Enter Access Code
          </button>
        )}
      </div>

      <form onSubmit={handlePreSubmit} className="submit-form">
        {/* Pre-filled Info */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Contact Information
          </h3>
          <p className="form-card-desc">Auto-filled from your profile</p>
          <div className="form-row">
            <div className="form-group">
              <label>Organization Name</label>
              <input type="text" value={user.organizationName} disabled />
            </div>
            <div className="form-group">
              <label>Contact Name</label>
              <input type="text" value={user.fullName} disabled />
            </div>
            <div className="form-group">
              <label>Contact Email</label>
              <input type="email" value={user.email} disabled />
            </div>
          </div>
        </div>

        {/* Domino Environment */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
            Domino Environment
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dominoServerVersion">Domino Server Version *</label>
              <select id="dominoServerVersion" name="dominoServerVersion" value={form.dominoServerVersion} onChange={handleChange}>
                <option value="">Select version...</option>
                <option value="v9.x">v9.x</option>
                <option value="v10.x">v10.x</option>
                <option value="v11.x">v11.x</option>
                <option value="v12.x">v12.x</option>
                <option value="Not Sure">Not Sure</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="migrateDatabaseData">Do you want to migrate the database as well ? *</label>
              <select id="migrateDatabaseData" name="migrateDatabaseData" value={form.migrateDatabaseData} onChange={handleChange}>
                <option value="">Select an option...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Sure">Not Sure</option>
              </select>
            </div>
          </div>
          <label className="checkbox-card">
            <input
              type="checkbox"
              name="dbAnalysisRequested"
              checked={form.dbAnalysisRequested}
              onChange={handleChange}
            />
            <div className="checkbox-card-content">
              <span className="checkbox-card-title">
                I also want a database analysis.
                <span className="inline-muted-hint"> (This is performed as a separate analysis engagement.)</span>
              </span>
            </div>
          </label>
        </div>

        {/* Application Identity */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            Application Identity
          </h3>
          <div className="form-group">
            <label htmlFor="applicationName">Application Name *</label>
            <input id="applicationName" name="applicationName" type="text" placeholder="e.g., HR Leave Requests" value={form.applicationName} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="applicationDescription">Application Description</label>
            <textarea id="applicationDescription" name="applicationDescription" rows="3" placeholder="Briefly describe what this application does..." value={form.applicationDescription} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="applicationTemplate">What template is this app based on?</label>
            <input id="applicationTemplate" name="applicationTemplate" type="text" placeholder="e.g., StdR6Mail, custom, unknown" value={form.applicationTemplate} onChange={handleChange} />
          </div>
        </div>

        {/* Usage & Scale */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M3 3v18h18"/><rect x="7" y="13" width="3" height="5"/><rect x="12" y="10" width="3" height="8"/><rect x="17" y="7" width="3" height="11"/></svg>
            Usage &amp; Scale
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="approximateUsers">Approximate number of active users</label>
              <input id="approximateUsers" name="approximateUsers" type="number" min="0" placeholder="e.g., 150" value={form.approximateUsers} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="approximateDocuments">Approximate number of documents/records</label>
              <input id="approximateDocuments" name="approximateDocuments" type="number" min="0" placeholder="e.g., 50000" value={form.approximateDocuments} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="approximateDatabaseSize">Approximate database size</label>
              <select id="approximateDatabaseSize" name="approximateDatabaseSize" value={form.approximateDatabaseSize} onChange={handleChange}>
                <option value="">Select size...</option>
                <option value="Under 100MB">Under 100MB</option>
                <option value="100MB-500MB">100MB-500MB</option>
                <option value="500MB-1GB">500MB-1GB</option>
                <option value="1-5GB">1-5GB</option>
                <option value="Over 5GB">Over 5GB</option>
                <option value="Not Sure">Not Sure</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="applicationCriticality">How critical is this application to daily operations?</label>
              <select id="applicationCriticality" name="applicationCriticality" value={form.applicationCriticality} onChange={handleChange}>
                <option value="">Select criticality...</option>
                <option value="Mission Critical - business stops without it">Mission Critical - business stops without it</option>
                <option value="Important - significant impact if unavailable">Important - significant impact if unavailable</option>
                <option value="Moderate - workaround exists">Moderate - workaround exists</option>
                <option value="Low - rarely used">Low - rarely used</option>
              </select>
            </div>
          </div>
        </div>

        {/* Application Features */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            Application Features
          </h3>
          <p className="form-card-desc">Select all that apply</p>
          <div className="checkbox-grid">
            {APP_FEATURE_OPTIONS.map((feature) => (
              <label className="checkbox-card" key={feature}>
                <input
                  type="checkbox"
                  checked={form.applicationFeatures.includes(feature)}
                  onChange={() => toggleFeature(feature)}
                />
                <div className="checkbox-card-content">
                  <span className="checkbox-card-title">{feature}</span>
                </div>
              </label>
            ))}
          </div>

          {form.applicationFeatures.includes('External Integration') && (
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label htmlFor="externalIntegrationsDetails">Describe the integrations briefly</label>
              <textarea id="externalIntegrationsDetails" name="externalIntegrationsDetails" rows="2" placeholder="Describe the integrations..." value={form.externalIntegrationsDetails} onChange={handleChange} />
            </div>
          )}
        </div>

        {/* Migration Preferences */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Migration Preferences
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="targetPlatform">Target Platform *</label>
              <select id="targetPlatform" name="targetPlatform" value={form.targetPlatform} onChange={handleChange}>
                <option value="">Select platform...</option>
                <option value="Revive React + Java [spring] + Mysql | H2">Revive React + Java [spring] + Mysql | H2</option>
                <option value="(BETA) Revive React + Java [spring] + Mongo" disabled title="Releasing soon">(BETA) Revive React + Java [spring] + Mongo</option>
                <option value="(BETA) Revive React + .Net + Mongo" disabled title="Releasing soon">(BETA) Revive React + .Net + Mongo</option>
                <option value="(BETA) Revive React + .Net + Mysql | H2" disabled title="Releasing soon">(BETA) Revive React + .Net + Mysql | H2</option>
                <option value="No preference">No preference</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="priority">Priority *</label>
              <select id="priority" name="priority" value={form.priority} onChange={handleChange}>
                <option value="">Select priority...</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="hasMigrationTeam">Does your organization have a migration team? *</label>
            <select id="hasMigrationTeam" name="hasMigrationTeam" value={form.hasMigrationTeam} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="No - Rivit handles everything">No - Rivit handles everything</option>
              <option value="Yes - internal team">Yes - internal team</option>
              <option value="Partially - collaborative approach">Partially - collaborative approach</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="preferredEngagement">Migration Delivery Model *</label>
            <select id="preferredEngagement" name="preferredEngagement" value={form.preferredEngagement} onChange={handleChange}>
              <option value="">Select engagement...</option>
              <option value="Full-Service - Rivit handles everything">Full-Service - Rivit handles everything</option>
              <option value="Guided - Rivit advises your team executes">Guided - Rivit advises your team executes</option>
              <option value="Collaborative - Joint effort">Collaborative - Joint effort</option>
              <option value="Assessment Only - Just the analysis report">Assessment Only - Just the analysis report</option>
            </select>
          </div>
        </div>

        {/* Timeline & Compliance */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Timeline &amp; Compliance
          </h3>
          <div className="form-group">
            <label htmlFor="desiredTimeline">Desired migration timeline *</label>
            <select id="desiredTimeline" name="desiredTimeline" value={form.desiredTimeline} onChange={handleChange}>
              <option value="">Select timeline...</option>
              <option value="ASAP">ASAP</option>
              <option value="Within 3 months">Within 3 months</option>
              <option value="Within 6 months">Within 6 months</option>
              <option value="Within 12 months">Within 12 months</option>
              <option value="No rush - planning phase">No rush - planning phase</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="hasComplianceRequirements">Are there regulatory or compliance requirements for this data? *</label>
            <select id="hasComplianceRequirements" name="hasComplianceRequirements" value={form.hasComplianceRequirements} onChange={handleChange}>
              <option value="">Select an option...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Not Sure">Not Sure</option>
            </select>
          </div>
          {form.hasComplianceRequirements === 'Yes' && (
            <div className="form-group">
              <label htmlFor="complianceDetails">Compliance requirements details</label>
              <textarea id="complianceDetails" name="complianceDetails" rows="2" placeholder="Describe regulatory or compliance requirements..." value={form.complianceDetails} onChange={handleChange} />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="additionalNotes">Additional Notes</label>
            <textarea id="additionalNotes" name="additionalNotes" rows="2" placeholder="Any additional context or requirements..." value={form.additionalNotes} onChange={handleChange} />
          </div>
        </div>

        {/* File Upload */}
        <div className="form-card">
          <h3 className="form-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            DXL File Upload
          </h3>
          <p className="form-card-desc">Upload the exported DXL file from your Lotus Notes application (.dxl, .xml, or .zip containing DXL files)</p>
          <div
            className={`file-drop-zone ${dxlFile && fileValid ? 'file-drop-zone-active' : ''} ${validationError ? 'file-drop-zone-error' : ''}`}
            onClick={() => !validating && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".dxl,.xml,.zip"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {dxlFile && fileValid ? (
              <div className="file-selected">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <polyline points="9 15 12 18 15 15" />
                </svg>
                <span className="file-name">{dxlFile.name}</span>
                <span className="file-size">{(dxlFile.size / 1024).toFixed(1)} KB</span>
                <span className="file-valid-badge">✓ Validated</span>
              </div>
            ) : (
              <div className="file-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>Click to upload or drag and drop</span>
                <span className="file-hint">.dxl, .xml, or .zip files up to 50MB</span>
              </div>
            )}
          </div>
          {validationError && (
            <div className="file-validation-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              <span>{validationError}</span>
            </div>
          )}
        </div>

        <div className="submit-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-lg" disabled={submitting || validating}>
            {submitting ? 'Submitting...' : 'Submit for Analysis'}
          </button>
        </div>
      </form>

      {/* Validation Overlay */}
      {validating && (
        <div className="dialog-overlay validation-overlay">
          <div className="validation-modal">
            <div className="spinner" />
            <h3>Validating file...</h3>
            <p>Checking file format and DXL structure</p>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="dialog-overlay" onClick={() => setShowConfirm(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h3 className="dialog-title">Confirm Submission</h3>
            <p className="dialog-message">
              You are about to submit <strong>{form.applicationName}</strong> for migration analysis. This will upload your DXL file for review.
            </p>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => setShowConfirm(false)}>Go Back</button>
              <button className="btn btn-primary" onClick={handleSubmit}>Confirm &amp; Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Access Code Modal */}
      {showAccessCodeModal && (
        <div className="dialog-overlay" onClick={() => remaining > 0 && setShowAccessCodeModal(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 className="dialog-title">Submission Limit Reached</h3>
            <p className="dialog-message">
              You've used all your free submissions. To continue, please enter an access code.
            </p>
            <p className="dialog-message" style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>
              Contact us to get an access code: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              {SUPPORT_PHONE && <> or call <a href={`tel:${SUPPORT_PHONE.replace(/\./g, '')}`}>{SUPPORT_PHONE}</a></>}
            </p>
            <div className="coupon-input-row">
              <input
                type="text"
                placeholder="Enter access code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="coupon-input"
                autoFocus
              />
              <button
                className="btn btn-primary"
                onClick={handleRedeemCode}
                disabled={redeemingCode || !accessCode.trim()}
              >
                {redeemingCode ? 'Applying...' : 'Apply'}
              </button>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => { setShowAccessCodeModal(false); navigate('/dashboard'); }}>Back to Dashboard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
